/** URL segment slug → kategori görünen adı (ürünler `category` alanı ile eşleşir) */
export const CATEGORY_SLUG_TO_NAME: Record<string, string> = {
  "berber-koltuklari": "Berber Koltukları",
  "berber-tezgahlari": "Berber Tezgahları",
  "kuafor-tezgahlari": "Kuaför Tezgahları",
  "makyaj-koltuklari": "Makyaj Koltukları",
  "tarama-koltuklari": "Tarama Koltukları",
  "cocuk-koltuklari": "Çocuk Koltukları",
  "cocuk-tezgahlari": "Çocuk Tezgahları",
  "bekleme-koltuklari": "Bekleme Koltukları",
  "yikama-setleri": "Yıkama Setleri",
  "yardimci-ekipmanlar": "Yardımcı Ekipmanlar",
  "laboratuvar-dolaplari": "Laboratuvar Dolapları",
  "spa-urunleri": "Spa Ürünleri",
  "agda-salon-urunleri": "Ağda Salon Ürünleri",
  "havluluk-ve-dolaplar": "Havluluk ve Dolaplar",
  "banko-kasiyer": "Banko & Kasiyer",
  "yedek-parcalar": "Yedek Parçalar",
  "lavabo-modelleri": "Lavabo Modelleri",
};

/** Kategori görünen adı → URL slug (middleware ve ürün breadcrumb için) */
export const CATEGORY_NAME_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(CATEGORY_SLUG_TO_NAME).map(([slug, name]) => [name, slug])
) as Record<string, string>;

export function getCategorySlugFromName(categoryName: string): string | undefined {
  return CATEGORY_NAME_TO_SLUG[categoryName];
}

export type CategoryIntentConfig = {
  primaryKeyword: string;
  secondaryKeywords: string[];
  searchIntent: "transactional" | "commercial";
  usp: string;
  intro: string;
  /** Ürün ızgarasının üstünde: hedef kelimeleri erken ve doğal kullanım */
  leadParagraphs?: string[];
  /** Alt rehber bölümüne ek paragraflar (benzersiz derinlik) */
  extraGuideParagraphs?: string[];
  buyingGuidePoints: string[];
  faq: { question: string; answer: string }[];
  relatedCategorySlugs: string[];
};

// Öncelikli SEO hedefleri için kategori bazlı niyet haritası.
export const CATEGORY_SEO_INTENT: Partial<Record<string, CategoryIntentConfig>> = {
  "berber-tezgahlari": {
    primaryKeyword: "berber tezgahı",
    secondaryKeywords: [
      "berber tezgahları",
      "berber tezgahı modelleri",
      "berber tezgahı fiyatları",
      "berber tezgahı ölçüleri",
      "berber salon tezgahı",
      "erkek kuaför tezgahı",
      "tekli berber tezgah fiyatları",
    ],
    searchIntent: "transactional",
    usp: "Özel ölçü üretim, dayanıklı gövde yapısı ve proje bazlı teklif desteği",
    intro:
      "Berber tezgahları ve berber tezgahı modelleri, salonunuzun çalışma hızını ve müşteri algısını doğrudan belirler. Filiz Kozmetik olarak erkek kuaför ve berber salonları için tezgah hatlarını; ayna, depolama, priz ve aydınlatmayı tek seferde doğru kurgulayacak şekilde planlıyoruz.",
    leadParagraphs: [
      "Berber tezgahları arayan işletmeler için öncelik, yoğun traş ve bakım trafiğinde stabil bir çalışma düzeni kurmaktır. Doğru berber tezgahı; personelin kol erişimini, ayna görüşünü ve tezgah üstü düzenini aynı anda optimize eder.",
      "Aşağıdaki ürünlerimizde farklı berber tezgahı modelleri ve ölçü seçenekleri bulabilirsiniz. Özel ölçü, renk ve modül sayısı için teknik çizim veya salon fotoğrafı paylaşmanız, size net berber tezgahı fiyatları ve termin bilgisi sunmamızı hızlandırır.",
    ],
    extraGuideParagraphs: [
      "Berber tezgahları siparişinde sık yapılan hata, sadece görseli beğenip elektrik / havalandırma / su tesisatı uyumunu son aşamaya bırakmaktır. Erken aşamada priz sayısı, kablo kanalı ve tezgah gövdesinin nem direnci netleştirildiğinde sahada sürpriz azalır.",
      "Berber salon tezgahı hatlarında çoklu istasyon düşünüyorsanız, çekmece ve dolap modüllerini simetrik veya kademeli yerleşimle planlamak hem stok hem de günlük temizlik rutinini kolaylaştırır.",
    ],
    buyingGuidePoints: [
      "Ayna genişliği, çekmece düzeni ve tezgah yüksekliğini personel boy ortalaması ve günlük iş akışına göre belirleyin.",
      "Neme, sıçramaya ve kimyasala dayanıklı gövde / kaplama seçin; özellikle yıkama ünitesine yakın istasyonlarda kritiktir.",
      "Elektrik ve priz yerleşimini tıraş makinesi, saç kurutma ve şarj ihtiyaçlarına göre planlayın; kablo görünürlüğünü minimumda tutun.",
      "Tezgah önü dolaşım genişliğini (sandalye dönüşü dahil) ölçerek planlayın; dar koridorlar hem personel hem müşteri konforunu düşürür.",
    ],
    faq: [
      {
        question: "Berber tezgahı seçerken hangi ölçüler önemlidir?",
        answer:
          "Ayna genişliği, tezgah derinliği ve çalışma yüksekliği günlük kullanım konforunu doğrudan etkiler. Salon planına göre ölçüleri özel üretimle uyarlamak en doğru yaklaşımdır.",
      },
      {
        question: "Berber tezgahı fiyatları neye göre değişir?",
        answer:
          "Kullanılan malzeme türü, modül sayısı, özel ölçü talepleri ve ek depolama çözümleri fiyatı belirleyen ana unsurlardır.",
      },
      {
        question: "Berber tezgahları ile kuaför tezgahları aynı mıdır?",
        answer:
          "Temel işlev benzer olsa da berber tezgahları genelde daha kompakt depolama ve erkek kuaför işlem hızına göre optimize edilir; kuaför tezgahlarında ürün çeşitliliği ve aydınlatma ihtiyacı daha fazla olabilir. Salon hizmetinize göre modül seçimi yapılır.",
      },
      {
        question: "Türkiye geneli sevkiyat mümkün mü?",
        answer:
          "Evet, paketleme ve montaj ihtiyacına göre lojistik planı teklif aşamasında netleştirilir. Şehir dışı projelerde kurulum desteği için ön bilgi paylaşmanız yeterlidir.",
      },
    ],
    relatedCategorySlugs: ["berber-koltuklari", "kuafor-tezgahlari", "yikama-setleri", "bekleme-koltuklari"],
  },
  "kuafor-tezgahlari": {
    primaryKeyword: "kuaför tezgahı",
    secondaryKeywords: [
      "kuaför tezgahları",
      "kuaför tezgahı modelleri",
      "kuaför tezgahı fiyatları",
      "kuaför salon tezgahı",
      "saç tasarım tezgahı",
      "bayan kuaför tezgahı",
      "bayan kuaför tezgah ölçüleri",
      "kuaför masası ölçüleri",
    ],
    searchIntent: "transactional",
    usp: "Kurumsal proje deneyimi, modern tasarım dili ve salon konseptine uygun üretim",
    intro:
      "Kuaför tezgahları ve kuaför tezgahı çözümleri, boya-bakım ve şekillendirme işlemlerinde düzenli bir çalışma hattı oluşturur. Filiz Kozmetik olarak kuaför salon tezgahı projelerinde ayna çizgisi, depolama ve aydınlatmayı birlikte ele alıyor, markanızın görsel diline uygun kuaför tezgahı modelleri sunuyoruz.",
    leadParagraphs: [
      "Kuaför tezgahları araması yapan salonlar için kritik nokta, hem çalışan ergonomisini hem de müşterinin aynadaki renk algısını doğru yakalamaktır. Saç tasarım tezgahı olarak planlanan ünitelerde LED sıcaklığı, ayna yüksekliği ve tezgah derinliği birlikte seçilmelidir.",
      "Bu sayfada çeşitli kuaför tezgahı modelleri listelenmektedir. Özel ölçü ve konsept için iletişimden randevu alarak kuaför tezgahı fiyatları ve üretim süresi hakkında net bilgi alabilirsiniz.",
    ],
    extraGuideParagraphs: [
      "Kuaför tezgahlarında çekmece içi organizasyon (fırça, boya, folyo, aksesuar) işlem süresini kısaltır. Yoğun salonlarda modül sayısını artırmak yerine doğru bölmeyi planlamak daha verimli olabilir.",
      "Kuaför salon tezgahı ile berber tezgahı aynı üretim hattından geçse de, bayan kuaför tezgahı ihtiyaçlarında genelde daha fazla ürün hacmi ve farklı aydınlatma senaryosu gerekir; teklif öncesi hizmet menünüzü paylaşmanız doğru modül seçimini kolaylaştırır.",
    ],
    buyingGuidePoints: [
      "Ayna aydınlatması ve kullanıcı görüş açısını salon genel ışığı ile birlikte değerlendirin; renk doğruluğu için doğru LED seçimi önemlidir.",
      "Cihaz ve ürün yerleşimi için çekmece ve raf modüllerini işlem yoğunluğuna göre kurgulayın; sık kullanılan malzemeleri tek elle erişilebilir tutun.",
      "Tezgah kaplama, metal ve kol kulp detaylarını salon konseptiyle tek dilde seçin; karışık profil ve renkler görsel karmaşa yaratır.",
      "Islak işlem veya yıkama ünitesine yakın tezgahlarda suya dayanıklı yüzey ve havalandırma planını önceden netleştirin.",
    ],
    faq: [
      {
        question: "Kuaför tezgahı ile berber tezgahı arasında ne fark vardır?",
        answer:
          "Kuaför tezgahları genellikle daha geniş depolama, farklı aydınlatma ve kozmetik ürün kullanımına uygun modüler detaylarla tasarlanır.",
      },
      {
        question: "Kuaför tezgahında özel ölçü yapılabiliyor mu?",
        answer:
          "Evet, salon planına göre genişlik, yükseklik ve modül dizilimi özel ölçü olarak üretilebilir.",
      },
      {
        question: "Kuaför tezgahı fiyatları için neler gerekir?",
        answer:
          "Adet, ölçü, malzeme seçimi, ayna tipi ve aydınlatma paketi fiyatı belirler. Yerleşim çizimi veya ölçü fotoğrafları ile aynı gün içinde ön teklif hazırlanabilir.",
      },
      {
        question: "Açık tezgah mı kapalı dolap mı tercih edilmeli?",
        answer:
          "Yoğun işlem gören salonlarda kapalı depolama toz ve düzeni kontrol altında tutar; vitrin etkisi istenen alanlarda açık raflar konsept ile dengelenebilir.",
      },
    ],
    relatedCategorySlugs: ["berber-tezgahlari", "makyaj-koltuklari", "tarama-koltuklari", "yikama-setleri"],
  },
  "berber-koltuklari": {
    primaryKeyword: "erkek kuaför dekorasyon",
    secondaryKeywords: [
      "berber koltukları",
      "berber koltuğu",
      "erkek kuaför koltukları",
      "erkek kuaför salon tasarımı",
      "barber chair modelleri",
      "berber koltuğu fiyatları",
      "berber koltukları fiyatları",
      "berber koltuğu imalatçıları",
    ],
    searchIntent: "commercial",
    usp: "Yoğun kullanıma uygun mekanik yapı, konfor ve konsept uyumlu model seçenekleri",
    intro:
      "Berber koltukları ve erkek kuaför dekorasyonu bir arada düşünüldüğünde salonunuzun hem Instagram vitrini hem de günlük operasyonu netleşir. Filiz Kozmetik olarak berber koltuğu seçiminde mekanik dayanım, döşeme ve görsel kimliği aynı pakette değerlendiriyoruz.",
    leadParagraphs: [
      "Berber koltukları arayan işletmeler için doğru berber koltuğu; hidrolik stabilite, kolay temizlenen döşeme ve müşteri oturuş konforunu birlikte sunmalıdır. Erkek kuaför koltukları segmentinde yoğun randevu trafiği, mekanik parçaların uzun ömürlü olmasını zorunlu kılar.",
      "Aşağıdaki modeller farklı berber koltuğu fiyatları ve tasarım dilleriyle gruplanabilir. Konsept çiziminizi paylaşarak erkek kuaför salon tasarımı ile uyumlu koltuk önerisi alabilirsiniz.",
    ],
    extraGuideParagraphs: [
      "Berber koltukları ile berber tezgahları aynı çizgide seçildiğinde salon bütünlüğü güçlenir; metal tonu, döşeme rengi ve ayak profili tek dilde tutulmalıdır.",
    ],
    buyingGuidePoints: [
      "Hidrolik sistem dayanımı ve taşıma kapasitesini günlük randevu yoğunluğuna göre seçin.",
      "Döşeme malzemesinde kolay temizlenebilir ve aşınmaya dirençli alternatifleri tercih edin.",
      "Ayaklık ve yatış açısı gibi ergonomi detaylarını verilen hizmet tipine göre değerlendirin.",
      "Servis erişimi olan modellerde yedek parça ve bakım planını önceden sorun; işletme kesinti riskini azaltır.",
    ],
    faq: [
      {
        question: "Erkek kuaför dekorasyonunda koltuk seçimi neden kritiktir?",
        answer:
          "Koltuklar hem görsel kimliğin merkezinde yer alır hem de hizmet süresince müşteri konforunu belirler; bu nedenle dayanıklılık ve tasarım birlikte ele alınmalıdır.",
      },
      {
        question: "Berber koltukları uzun süreli kullanıma uygun mu?",
        answer:
          "Profesyonel kullanım için üretilen modeller, mekanik dayanım ve döşeme kalitesiyle yoğun salon temposuna uygundur.",
      },
      {
        question: "Berber koltuğu imalatçılarından alım yapmanın avantajı nedir?",
        answer:
          "Filiz Kozmetik gibi doğrudan imalatçı firmalardan alım yaptığınızda, toptan berber koltuğu fiyatları avantajından yararlanır, yedek parça ve garanti sürecinde doğrudan üretici muhatap bulursunuz.",
      },
    ],
    relatedCategorySlugs: ["berber-tezgahlari", "bekleme-koltuklari", "yardimci-ekipmanlar"],
  },
  "makyaj-koltuklari": {
    primaryKeyword: "makyaj koltuğu",
    secondaryKeywords: [
      "güzellik salonu koltuğu",
      "makyaj koltuğu modelleri",
      "güzellik merkezi ekipmanları",
      "profesyonel makyaj koltuğu",
      "güzellik salonu dekorasyon",
      "makyaj koltuğu fiyatları",
    ],
    searchIntent: "commercial",
    usp: "Konfor, estetik ve uygulama ergonomisini bir araya getiren salon çözümleri",
    intro:
      "Güzellik salonu dekorasyon projelerinde makyaj koltukları, alanın premium algısını ve hizmet kalitesini doğrudan etkileyen ana ekipmanlardan biridir. Uzun süreli işlemlerde müşteri konforu ve uzmanın çalışma açısı kritik öneme sahiptir.",
    leadParagraphs: [
      "Makyaj koltukları arayan işletmeler için en önemli kriter, koltuğun sırt, başlık ve yükseklik ayarlarının hassasiyetidir. Müşterinin rahat bir pozisyonda sabit kalması, profesyonel işlemlerin kusursuz tamamlanmasını sağlar.",
      "Aşağıdaki modellerimizde salonunuzun vizyonuna uyum sağlayacak çeşitli makyaj koltuğu fiyatları ve tasarımları bulunmaktadır. Estetik kadar hijyenik yüzey kaplamalarını da özenle seçiyoruz.",
    ],
    extraGuideParagraphs: [
      "Makyaj koltuklarının aydınlatma sistemleriyle entegre düşünülmesi, gölge oyunlarını engeller ve makyaj uzmanının renkleri doğru görmesini sağlar.",
    ],
    buyingGuidePoints: [
      "Uygulama süresine uygun, uzun süreli oturumlarda konfor sunan ergonomik modeller tercih edin.",
      "Aydınlatma ve ayna kombinasyonu ile uyumlu renk/kaplama seçenekleri belirleyin.",
      "Hijyen standartları için kolay temizlenen, leke tutmayan, antibakteriyel yüzeyler seçin.",
      "Başlık ayarının farklı işlem tipleri (makyaj, kaş, kalıcı makyaj) için yeterince esnek olduğundan emin olun."
    ],
    faq: [
      {
        question: "Güzellik salonu dekorasyonunda ekipman sıralaması nasıl yapılmalı?",
        answer:
          "İşlem akışına göre koltuk, tezgah ve depolama alanları kullanıcı dolaşımını engellemeyecek şekilde konumlandırılmalıdır.",
      },
      {
        question: "Makyaj koltuğu alırken nelere dikkat edilmeli?",
        answer:
          "Ergonomi, döşeme kalitesi, hidrolik yükseklik ayarı, yatış açısı ve genel salon konseptiyle uyum en kritik seçim kriterleridir.",
      },
    ],
    relatedCategorySlugs: ["kuafor-tezgahlari", "spa-urunleri", "yardimci-ekipmanlar"],
  },
  "tarama-koltuklari": {
    primaryKeyword: "tarama koltuğu",
    secondaryKeywords: [
      "kuaför tarama koltukları",
      "saç kesim koltuğu",
      "bayan kuaför koltukları",
      "tarama koltuğu fiyatları",
      "kuaför sandalyesi",
    ],
    searchIntent: "transactional",
    usp: "Ergonomik tasarım, paslanmaz mekanizma ve geniş renk yelpazesi",
    intro:
      "Tarama koltukları, bir kuaför salonunun kalbidir. Saç kesimi ve tarama işlemleri sırasında müşterinize üst düzey konfor sunarken, kuaförün hareket kabiliyetini maksimize eder.",
    leadParagraphs: [
      "Kuaför tarama koltukları seçimi, doğrudan işlem kalitesini etkiler. Saç kesim koltuğu olarak da bilinen bu ürünlerde sırt açısı, pompa dayanıklılığı ve ayağın yere sağlam basması en önemli mühendislik detaylarıdır.",
      "Listelediğimiz modellerimiz arasından bayan kuaför koltukları için salon tasarımınıza uyacak geniş bir renk ve kaplama seçeneği bulabilirsiniz. Hidrolik sistemlerimiz ağır kullanıma uygundur."
    ],
    extraGuideParagraphs: [
      "Tarama koltuklarında kullanılan sünger kalitesi, yıllar geçse bile koltuğun formunu korumasını sağlar. Çökme yapmayan yüksek dansiteli süngerler tercih edilmelidir.",
      "Koltuk ayaklarında kare, yuvarlak veya yıldız ayak seçenekleri bulunur. Kuaför salonunuzun zeminine ve tasarım diline uygun olanı seçmek görsel bütünlük için önemlidir."
    ],
    buyingGuidePoints: [
      "Saç kesimi sırasında boyun ve omuzları yormayacak sırt yüksekliğine sahip modeller seçin.",
      "Yere dökülen saçların kolay süpürülmesi için taban tasarımının temizliğe uygun olmasına dikkat edin.",
      "Kullanılacak fön, maşa gibi ısıl işlemlere ve kimyasallara karşı dayanıklı kaplama tercih edin.",
    ],
    faq: [
      {
        question: "Tarama koltuğu ile berber koltuğu arasındaki fark nedir?",
        answer:
          "Berber koltuklarında genellikle tıraş işlemi için yatma özelliği ve başlık bulunur. Tarama koltukları ise saç kesimi ve şekillendirmeye yönelik, daha pratik ve dik açılıdır.",
      },
      {
        question: "Tarama koltuklarında ayak çeşitleri nelerdir?",
        answer:
          "Yıldız ayak, yuvarlak ayak ve kare ayak seçenekleri mevcuttur. Tamamı amortisörlü ve hidrolik sistemle donatılmıştır.",
      },
    ],
    relatedCategorySlugs: ["kuafor-tezgahlari", "yikama-setleri", "bekleme-koltuklari"],
  },
  "cocuk-koltuklari": {
    primaryKeyword: "çocuk berber koltuğu",
    secondaryKeywords: [
      "çocuk kuaför koltukları",
      "çocuk saç kesim koltuğu",
      "araba şeklinde çocuk koltuğu",
      "eğlenceli berber koltukları",
      "çocuk berber koltuğu fiyatları",
    ],
    searchIntent: "transactional",
    usp: "Çocuklar için eğlenceli, güvenli ve salon konseptine uyumlu tasarımlar",
    intro:
      "Çocuk koltukları, küçük misafirlerinizin saç kesim deneyimini keyifli bir oyuna dönüştürür. Güvenlik ve eğlenceyi bir araya getiren tasarımlar, salonunuzun aile dostu imajını güçlendirir.",
    leadParagraphs: [
      "Çocuk kuaför koltukları alırken ilk dikkat edilmesi gereken nokta güvenliktir. Emniyet kemerleri, denge merkezi ve sivri köşe barındırmayan tasarımlar şarttır.",
      "Araba şeklinde çocuk koltuğu gibi eğlenceli modeller, çocukların korkularını yenmesine yardımcı olur. Operasyon sürecini hızlandırarak personelin işini kolaylaştırır."
    ],
    extraGuideParagraphs: [
      "Standart yetişkin tezgahlarıyla da kullanılabilen çocuk koltuklarında hidrolik ayar, çocuğun aynada kendini görmesini ve kesim yapan uzman için doğru açıyı yakalamasını sağlar.",
    ],
    buyingGuidePoints: [
      "Kesinlikle emniyet kemeri veya koruma barı olan modelleri tercih edin.",
      "Hareketli çocukların güvenliği için koltuğun zemin dengesinin kusursuz olduğundan emin olun.",
      "Kolay temizlenebilen ve eğlenceli figürlere sahip kaplamalar seçin.",
    ],
    faq: [
      {
        question: "Çocuk koltukları yetişkin tezgahlarına uyumlu mu?",
        answer:
          "Evet, yükseltme amortisörleri sayesinde standart tezgahlarla rahatça kullanılabilir.",
      },
      {
        question: "Çocuk koltuğu kaç yaşa kadar uygundur?",
        answer:
          "Modele bağlı olmakla birlikte genellikle 1-7 yaş arası çocuklar için güvenli ve ergonomik kullanım sağlar.",
      },
    ],
    relatedCategorySlugs: ["cocuk-tezgahlari", "berber-koltuklari"],
  },
  "cocuk-tezgahlari": {
    primaryKeyword: "çocuk kuaför tezgahı",
    secondaryKeywords: [
      "çocuk berber tezgahı",
      "eğlenceli saç kesim tezgahları",
      "çocuk kuaförü dekorasyonu",
      "çocuk salonu tezgahları",
    ],
    searchIntent: "commercial",
    usp: "Çocuk dünyasına hitap eden renkli, güvenli ve dayanıklı tezgah çözümleri",
    intro:
      "Çocuk tezgahları, minik müşterilerinizin ilgisini çekecek ekranlar, aynalar ve renkli detaylarla donatılır. Çocuk kuaförü dekorasyonunda fark yaratan en önemli modüllerden biridir.",
    leadParagraphs: [
      "Çocuk kuaför tezgahı tasarımı, çocukların dikkatini çekecek tablet veya ekran entegrasyonlarına uygun olmalıdır. Ayrıca sivri köşelerden arındırılmış güvenlikli üretim esastır.",
      "Eğlenceli saç kesim tezgahları ile küçük yaş grubu müşterilerinizin ağlamadan, keyifle hizmet almasını sağlayabilirsiniz."
    ],
    buyingGuidePoints: [
      "Köşeleri yuvarlatılmış, çocuk çarpmalarına karşı güvenli tasarımları seçin.",
      "Çizgi film izlemeleri için tablet veya ekran monte edilebilir alanlar olmasına dikkat edin.",
      "Renkli, canlı ve çocukların ilgisini çeken görsel temalar kullanın.",
    ],
    faq: [
      {
        question: "Çocuk tezgahlarına ekran eklenebilir mi?",
        answer:
          "Evet, tasarımlarımız talebinize göre tablet tutucu veya entegre monitör alanlarıyla özel olarak üretilebilir.",
      },
    ],
    relatedCategorySlugs: ["cocuk-koltuklari", "berber-tezgahlari"],
  },
  "bekleme-koltuklari": {
    primaryKeyword: "kuaför bekleme koltuğu",
    secondaryKeywords: [
      "bekleme koltukları",
      "salon bekleme alanı",
      "berber bekleme koltukları",
      "ikili bekleme koltuğu",
      "üçlü bekleme koltuğu",
    ],
    searchIntent: "transactional",
    usp: "Konforlu bekleme alanları için misafir ağırlama çözümleri ve lüks tasarımlar",
    intro:
      "Bekleme koltukları, müşterilerinizin salonunuza adım attığı andaki ilk izlenimidir. Şık ve konforlu bir bekleme alanı, hizmet kalitenizin habercisidir.",
    leadParagraphs: [
      "Kuaför bekleme koltuğu seçerken salonunuzun metrekare büyüklüğü ve günlük müşteri sirkülasyonu baz alınmalıdır. İkili veya üçlü bekleme koltuğu seçenekleriyle alanı en verimli şekilde kullanabilirsiniz.",
      "İç tasarımınızı tamamlayacak deri veya kumaş kaplama alternatifleriyle, bekleme süresini misafirleriniz için keyifli bir dinlenme anına dönüştürün."
    ],
    extraGuideParagraphs: [
      "Bekleme alanlarında genellikle orta sehpa veya dergilik modülleri ile kombin yapılabilecek koltuk takımları tercih edilir. Renk uyumu salonun premium algısını yükseltir.",
    ],
    buyingGuidePoints: [
      "Giriş alanınızın ölçülerine uygun puf, tekli, ikili veya üçlü formasyonlar planlayın.",
      "Kolay temizlenebilen, sıvı itici özellikli döşeme kumaşları tercih edin.",
      "Uzun süre beklemelerde bile rahatsızlık vermeyecek ergonomik sırt destekleri olmasına dikkat edin.",
    ],
    faq: [
      {
        question: "Bekleme koltuklarında kumaş değişimi yapılıyor mu?",
        answer:
          "Evet, salon konseptinize en uygun renkte ve dokuda kumaş/suni deri seçimi yapabilirsiniz.",
      },
      {
        question: "Dar alanlar için bekleme koltuğu çözümünüz var mı?",
        answer:
          "Küçük metrekareli salonlar için sırtsız puf modelleri veya ince hatlı tekli koltuk tasarımları mevcuttur.",
      },
    ],
    relatedCategorySlugs: ["berber-koltuklari", "kuafor-tezgahlari", "banko-kasiyer"],
  },
  "yikama-setleri": {
    primaryKeyword: "yıkama seti",
    secondaryKeywords: [
      "kuaför yıkama koltuğu",
      "saç yıkama seti",
      "berber yıkama ünitesi",
      "masajlı yıkama seti",
      "baş yıkama koltukları",
    ],
    searchIntent: "commercial",
    usp: "Omurga dostu ergonomik yatış, geniş seramik lavabo ve paslanmaz aksam",
    intro:
      "Yıkama setleri, saç bakımının ve temizliğinin merkezidir. Kuaför yıkama koltuğu modellerimizde müşterinin boyun konforunu sağlarken, operatörün sırt sağlığını koruyacak yüksekliğe önem veriyoruz.",
    leadParagraphs: [
      "Saç yıkama seti arayışınızda lavabonun derinliği ve boyunluk silikonunun kalitesi büyük fark yaratır. Su sıçramasını engelleyen derin seramikler ve ergonomik yatış pozisyonları lüks bir deneyim sunar.",
      "Gelişmiş masajlı yıkama seti modelleri, müşterilerinize sıradan bir yıkama işleminden çok daha fazlasını, adeta bir SPA deneyimi vaat eder."
    ],
    extraGuideParagraphs: [
      "Yıkama ünitelerinin yerleşimi salonun su tesisatına doğrudan bağlıdır. Alttan çıkışlı veya arkadan çıkışlı tesisat durumunuza göre doğru modeli seçmek, kurulumda kırıp dökme işlemlerini önler.",
    ],
    buyingGuidePoints: [
      "Boyun ağrısına sebep olmayan, kaliteli silikon başlığa sahip geniş seramikli modelleri tercih edin.",
      "Batarya ve duş başlıklarında su tasarruflu ve paslanmaz materyal kullanıldığından emin olun.",
      "Kuaför için çalışma yüksekliğinin uygun olduğu, eğilme gerektirmeyen gövdeler seçin.",
    ],
    faq: [
      {
        question: "Yıkama seti kurulumu için altyapı nasıl olmalı?",
        answer:
          "Temiz ve pis su giderlerinin ünitenin yerleşeceği noktada, genellikle zeminden hazır olması gerekir. Ürün ölçülerine göre teknik çizim sağlıyoruz.",
      },
      {
        question: "Yıkama setlerinde masaj özelliği eklenebiliyor mu?",
        answer:
          "Evet, VIP serisi yıkama setlerimizde titreşimli masaj motorları opsiyonel olarak eklenebilmektedir.",
      },
    ],
    relatedCategorySlugs: ["kuafor-tezgahlari", "berber-tezgahlari", "havluluk-ve-dolaplar"],
  },
  "yardimci-ekipmanlar": {
    primaryKeyword: "kuaför yardımcı ekipmanları",
    secondaryKeywords: [
      "kuaför el arabası",
      "berber servis arabası",
      "boya arabası",
      "salon taşıma arabası",
      "kuaför taburesi",
    ],
    searchIntent: "transactional",
    usp: "Pratik depolama, sessiz tekerlekler ve iş akışını hızlandıran mobilite",
    intro:
      "Yardımcı ekipmanlar, bir salonda işleri sessizce yürüten kahramanlardır. Boya arabaları, servis tepsileri ve uzman tabureleri günlük performansı büyük ölçüde artırır.",
    leadParagraphs: [
      "Kuaför el arabası ve boya servis arabası gibi mobil üniteler, işlem sırasında ihtiyacınız olan her şeyi elinizin altında tutar. Tekerleklerin sessiz olması ve saç dolanmayan sisteme sahip olması kritik öneme sahiptir.",
      "Ayrıca uzun işlemler için tasarlanmış ergonomik kuaför taburesi modellerimiz, ayakta uzun süre kalmanın verdiği yorgunluğu minimize eder."
    ],
    buyingGuidePoints: [
      "Kıl, saç dolanmayan ve iz bırakmayan özel tekerlekli arabaları tercih edin.",
      "Boya kimyasallarına dayanıklı ABS plastik veya paslanmaz çelik tepsiler kullanın.",
      "Kuaför taburelerinde sırt destekli ve yüksekliği ayarlanabilir gazlı amortisörlere dikkat edin.",
    ],
    faq: [
      {
        question: "Servis arabalarının temizliği nasıl yapılmalı?",
        answer:
          "Kimyasal dökülmelerine dayanıklı malzemelerden üretildikleri için nemli bez ve hafif temizleyicilerle kolayca silinebilir.",
      },
    ],
    relatedCategorySlugs: ["kuafor-tezgahlari", "makyaj-koltuklari"],
  },
  "laboratuvar-dolaplari": {
    primaryKeyword: "kuaför laboratuvar dolabı",
    secondaryKeywords: [
      "boya hazırlama dolabı",
      "kuaför havluluk dolabı",
      "ürün sergileme dolapları",
      "salon mutfak dolabı",
    ],
    searchIntent: "transactional",
    usp: "Maksimum depolama alanı, kimyasala dirençli tezgahlar ve düzen",
    intro:
      "Laboratuvar dolapları, kuaför salonlarının görünmeyen kalbidir. Boya hazırlama, havlu depolama ve kimyasal yönetimi için güvenli, düzenli ve şık alanlar yaratır.",
    leadParagraphs: [
      "Boya hazırlama dolabı olarak kullanılan laboratuvar bölümleri, renk tüplerinin organize edildiği, atık yönetiminin sağlandığı alanlardır. Lavabolu ve çöp hazneli modüller iş akışını mükemmelleştirir.",
      "Projenize özel ölçülerde üretilen bu dolaplar, su ve kimyasallara dayanıklı tezgah yüzeyleri ile uzun yıllar sorunsuz hizmet verir."
    ],
    extraGuideParagraphs: [
      "Açık vitrin ve kapalı depolamayı harmanlayan dolaplar sayesinde stok takibi çok daha kolay yapılır.",
    ],
    buyingGuidePoints: [
      "Boya hazırlık tezgahlarında leke tutmayan korian veya kaliteli akrilik tezgahlar tercih edin.",
      "Bol çekmeceli ve özel boya tüp rafı içeren modüllerle stok kontrolünü sağlayın.",
      "Mekanizmaların (menteşe/ray) neme dayanıklı ve yavaşlatıcılı olmasına özen gösterin.",
    ],
    faq: [
      {
        question: "Laboratuvar dolapları salon ölçüsüne göre üretilir mi?",
        answer:
          "Evet, tüm dolaplarımız salonunuzun duvar ölçülerine ve tesisat yerleşimine göre özel olarak projelendirilir.",
      },
    ],
    relatedCategorySlugs: ["yikama-setleri", "havluluk-ve-dolaplar"],
  },
  "spa-urunleri": {
    primaryKeyword: "spa yatağı",
    secondaryKeywords: [
      "masaj yatağı modelleri",
      "cilt bakım yatağı",
      "spa merkezi ekipmanları",
      "motorlu masaj yatağı",
    ],
    searchIntent: "commercial",
    usp: "Sessiz motor mekanizmaları, ultra konforlu sünger yapısı ve lüks doku",
    intro:
      "Spa ürünleri, lüks ve rahatlamanın zirvesini temsil eder. Spa yatağı ve masaj masası seçeneklerimizle, müşterilerinize unutulmaz bir rahatlama deneyimi sunabilirsiniz.",
    leadParagraphs: [
      "Bir masaj yatağı veya cilt bakım yatağı seçerken, sadece müşterinin değil, terapistin de çalışma açısı dikkate alınmalıdır. Yüksekliği ayarlanabilir motorlu yataklar, uygulamalardaki verimi artırır.",
      "Sırt ve ayak kısımları hareketli modellerimizle farklı vücut ve yüz bakımlarını tek bir yatak üzerinde kolaylıkla gerçekleştirebilirsiniz."
    ],
    buyingGuidePoints: [
      "Sessiz ve sarsıntısız çalışan, uzun ömürlü motor sistemlerine sahip ürünleri inceleyin.",
      "Vücudun şeklini alan, kalın ve terletmeyen özel visco veya yüksek kalite süngerli yatakları seçin.",
      "Yağ ve kozmetik ürünlerin leke bırakmasını engelleyen kaplama materyalleri tercih edin.",
    ],
    faq: [
      {
        question: "Motorlu spa yataklarının garantisi var mı?",
        answer:
          "Tüm motor ve elektronik aksamlarımız, üretim ve montaj hatalarına karşı 2 yıl garantilidir.",
      },
    ],
    relatedCategorySlugs: ["makyaj-koltuklari", "yardimci-ekipmanlar"],
  },
  "agda-salon-urunleri": {
    primaryKeyword: "ağda yatağı",
    secondaryKeywords: [
      "ağda salonu dekorasyonu",
      "cilt bakım sedyesi",
      "ağda odası ekipmanları",
      "katlanabilir sedye",
    ],
    searchIntent: "transactional",
    usp: "Hijyenik yüzeyler, stabil gövde ve pratik kullanım özellikleri",
    intro:
      "Ağda salon ürünleri, pratiklik ve yüksek hijyen standartlarının gerektiği ortamlar için özel üretilir. Ağda sedyelerimiz dar alanlarda bile maksimum verimlilik sunar.",
    leadParagraphs: [
      "Bir ağda yatağı veya sedyesinin en önemli özelliği sallanmaz ve dayanıklı olmasıdır. Sabit ayaklı veya tekerlekli (kilitlenebilir) modeller arasından salonunuzun ihtiyacına uygun olanı seçebilirsiniz.",
      "Kolay temizlenebilir kağıt havluluk aparatları, işlemin hijyenini destekler."
    ],
    buyingGuidePoints: [
      "Muayene bezi ve havlu kağıt takmak için entegre aparatlı modeller işinizi hızlandırır.",
      "Antibakteriyel ve kolay silinebilir suni deri kaplamalar tercih edilmelidir.",
    ],
    faq: [
      {
        question: "Sedyelerin taşıma kapasitesi ne kadardır?",
        answer:
          "Standart ve güçlendirilmiş modellerimiz genellikle 150-200 kg arası taşıma kapasitesine sahiptir.",
      },
    ],
    relatedCategorySlugs: ["spa-urunleri", "yardimci-ekipmanlar"],
  },
  "havluluk-ve-dolaplar": {
    primaryKeyword: "kuaför havluluk dolabı",
    secondaryKeywords: [
      "havlu rafı",
      "salon depolama dolapları",
      "ürün teşhir dolabı",
      "temiz kirli havlu sepeti",
    ],
    searchIntent: "transactional",
    usp: "Yer kazandıran çözümler, kirli/temiz ayrımı sağlayan akıllı tasarımlar",
    intro:
      "Havluluk ve dolaplar, günlük salon trafiğinde düzeni sağlayan sessiz asistanlardır. Şık teşhir alanları ve pratik havlu üniteleri ile salonunuz daima derli toplu görünür.",
    leadParagraphs: [
      "Kuaför havluluk dolabı seçiminde, temiz havluların estetik bir şekilde dizilebilmesi kadar, kirli havluların da gizlenebildiği mekanizmalar önemlidir. Altı çöp sepetli veya kirli sepetli modeller favoridir.",
      "Ürün satışınızı artıracak şık LED aydınlatmalı perakende ürün teşhir dolapları da bu grubun vazgeçilmezidir."
    ],
    buyingGuidePoints: [
      "Müşterilerin görebileceği açık vitrin alanları ile ürün satışlarınızı destekleyin.",
      "Menteşe kapaklı bölmelerde kirli havluları gizleyen pratik kapak mekanizmalarına sahip modüller seçin.",
    ],
    faq: [
      {
        question: "Havluluklar suya dayanıklı malzemeden mi yapılıyor?",
        answer:
          "Nemli ortamlar düşünülerek MDF üzeri suya ve buhara dirençli kaliteli kaplamalar kullanılmaktadır.",
      },
    ],
    relatedCategorySlugs: ["yikama-setleri", "laboratuvar-dolaplari"],
  },
  "banko-kasiyer": {
    primaryKeyword: "kuaför karşılama bankosu",
    secondaryKeywords: [
      "resepsiyon bankosu",
      "kasa bankosu",
      "kasiyer masası",
      "salon giriş bankosu",
    ],
    searchIntent: "transactional",
    usp: "Salonun kimliğini yansıtan şık karşılama alanları, güvenli nakit çekmeceleri",
    intro:
      "Banko ve kasiyer bölümleri, müşterinizin salonunuzla kurduğu ilk ve son temas noktasıdır. Kuaför karşılama bankosu modellerimiz, profesyonelliği ve misafirperverliği aynı anda hissettirir.",
    leadParagraphs: [
      "Bir resepsiyon bankosu, markanızın logosunu en iyi şekilde taşıyacak ışıklı ve geniş ön yüzeylere sahip olmalıdır. Ayrıca arka kısımda randevu defteri, POS cihazı ve bilgisayar için ergonomik bir çalışma alanı sunmalıdır.",
      "İç kısımda yer alan kilitli çekmeceler, güvenli nakit kontrolü sağlarken kablo gizleme yollarıyla estetik bir masa üstü görünümü elde edilir."
    ],
    extraGuideParagraphs: [
      "Banko tasarımının arkasında yer alan ürün satış vitrinleri, müşteriler hesabı öderken perakende ürün satış potansiyelinizi ciddi şekilde artırır.",
    ],
    buyingGuidePoints: [
      "Müşteri görüş açısını kapatmayan, iletişim kurmayı kolaylaştıran yükseklikte bankolar seçin.",
      "Giriş alanında kablo karmaşasını önleyecek kablo kanalı ve priz çıkışlı sistemlere dikkat edin.",
      "Kişiselleştirilebilir logo alanına (LED aydınlatmalı vb.) sahip tasarımlarla marka değerini artırın.",
    ],
    faq: [
      {
        question: "Karşılama bankolarına logomuzu ekleyebiliyor musunuz?",
        answer:
          "Kesinlikle. Plexi kabartma veya LED aydınlatmalı olarak marka logonuzu banko ön yüzeyine entegre edebiliyoruz.",
      },
    ],
    relatedCategorySlugs: ["bekleme-koltuklari"],
  },
  "yedek-parcalar": {
    primaryKeyword: "kuaför koltuğu yedek parça",
    secondaryKeywords: [
      "amortisör",
      "yıldız ayak",
      "yıkama silikonu",
      "berber koltuğu pompası",
    ],
    searchIntent: "transactional",
    usp: "Orijinal uyumlu parçalar, hızlı tedarik ve uzun ömürlü kullanım",
    intro:
      "Yedek parçalar, işletmenizin kesintisiz hizmet vermesi için hayati önem taşır. Kuaför ve berber koltuklarınızın veya yıkama setlerinizin bakımı için gereken tüm aksamları sağlıyoruz.",
    leadParagraphs: [
      "Zamanla aşınan hidrolik pompalar, yıpranan boyun silikonları veya kırılan tekerlekler, orijinal kuaför koltuğu yedek parça çözümleriyle kolayca yenilenebilir.",
      "Uzun ömürlü kullanım için amortisörler, yıldız ve yuvarlak ayaklar gibi temel mekanik parçaları stoklu olarak sunuyoruz."
    ],
    buyingGuidePoints: [
      "Koltuğunuzun veya ünitenizin marka/model uyumundan emin olmak için görsel paylaşarak sipariş verin.",
      "Yıkama üniteleri için batarya ve duş başlıklarında su sızdırmazlık contalarının tam olduğundan emin olun.",
    ],
    faq: [
      {
        question: "Farklı marka koltuklar için yedek parça bulunuyor mu?",
        answer:
          "Hidrolik pompa ve ayak gibi parçaların büyük kısmı evrensel ölçülerdedir, ancak kesin uyum için bağlantı ölçülerini kontrol etmemiz gerekir.",
      },
    ],
    relatedCategorySlugs: ["berber-koltuklari", "tarama-koltuklari", "yikama-setleri"],
  },
  "lavabo-modelleri": {
    primaryKeyword: "kuaför yıkama lavabosu",
    secondaryKeywords: [
      "seramik lavabo",
      "berber tezgah lavabosu",
      "siyah yıkama lavabosu",
      "boyunluklu lavabo",
    ],
    searchIntent: "transactional",
    usp: "Leke tutmayan sır yapısı, ergonomik boyun kavisleri ve derin hazne",
    intro:
      "Lavabo modelleri, tezgah altı, tezgah üstü veya yıkama setleri için özel seramikten üretilir. Kaliteli bir lavabo, saç boyası ve kimyasallara karşı yıllarca formunu ve rengini korur.",
    leadParagraphs: [
      "Kuaför yıkama lavabosu seçeneklerinde derinlik, su sıçratmasını önleyen temel faktördür. Aynı zamanda ergonomik boyun kavisi, yıkama işleminin olmazsa olmazıdır.",
      "Berber tezgahlarında kullanılan tezgah altı veya üstü lavabolar, kullanım pratikliği ve şık görünüm sunarken; siyah seramik lavabo gibi seçenekler modern salonların favorisidir."
    ],
    buyingGuidePoints: [
      "Çatlama yapmayan, yüksek ısıda fırınlanmış kalın seramik modelleri tercih edin.",
      "Boyun konforu için silikon takılabilecek standart kavis ölçüsüne sahip tasarımları seçin.",
    ],
    faq: [
      {
        question: "Lavabolar süzgeç ve batarya ile birlikte mi satılıyor?",
        answer:
          "Seramik lavabolar standart olarak tek satılır, ancak süzgeç ve kuaför bataryaları isteğe göre sete eklenebilir.",
      },
    ],
    relatedCategorySlugs: ["yikama-setleri", "berber-tezgahlari"],
  },
};
